import {MutableRefObject, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react"

// extracted from https://github.com/sergeyleschev/react-custom-hooks/blob/main/src/hooks/useTimeout/useTimeout.js
const useTimeout = (callback: Function, delay: number) => {
    const callbackRef = useRef(callback)
    const timeoutRef = useRef<NodeJS.Timeout>()
    useEffect(() => {
        callbackRef.current = callback
    }, [callback])
    const set = useCallback(() => {
        timeoutRef.current = setTimeout(() => callbackRef.current(), delay)
    }, [delay])
    const clear = useCallback(() => {
        timeoutRef.current && clearTimeout(timeoutRef.current)
    }, [])
    useEffect(() => {
        set()
        return clear
    }, [delay, set, clear])
    const reset = useCallback(() => {
        clear()
        set()
    }, [clear, set])
    return {reset, clear}
}

const useDetectScrollDirection = () => {
    const [scrollDirection, setScrollDirection] = useState<string | null>(null);
    const thresholdPx = 5;

    useEffect(() => {
        let lastScrollY = window.scrollY;

        const updateScrollDirection = () => {
            const scrollY = window.scrollY;
            const direction = scrollY > lastScrollY ? "down" : "up";
            if (direction !== scrollDirection && (scrollY - lastScrollY > thresholdPx || scrollY - lastScrollY < -thresholdPx)) {
                setScrollDirection(direction);
            }
            lastScrollY = scrollY > 0 ? scrollY : 0;
        };
        window.addEventListener("scroll", updateScrollDirection);
        return () => {
            window.removeEventListener("scroll", updateScrollDirection);
        }
    }, [scrollDirection]);

    return scrollDirection;
};

const useIsOverflowDetected = (ref: MutableRefObject<HTMLElement | undefined>) => {

    const [isOverflowDetected, setIsOverflowDetected] = useState<boolean>(false);

    useLayoutEffect(() => {
        const detectVerticalOverflow = () => {
            setIsOverflowDetected(!!ref.current && ref.current.scrollHeight > ref.current.clientHeight);
        };

        detectVerticalOverflow();

        if (ref.current) {
            new ResizeObserver(detectVerticalOverflow).observe(ref.current);
        }

    }, [ref]);

    return isOverflowDetected;
};

const usePrevious = (value: any) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

const getPublicUrl = (path: string, encodeFinalPart: boolean = false) => {
    if (encodeFinalPart) {
        const pathPrefix = path.split("/").splice(0, 2).join("/");
        const encodedFileName= encodeURIComponent(path.split("/").splice(2).join("/"));
        path = [pathPrefix, encodedFileName].join("/");
    }
    return [
        (!!process.env.REACT_APP_DATA_STORAGE_URL) ?
            process.env.REACT_APP_DATA_STORAGE_URL!.replace(/\/+$/g, "") :
            window.location.origin,
        "data",
        path.replace(/^\/+/g, ""),
    ].join("/");
}

const getAssetDownloadUrl = (path: string) => (!!process.env.REACT_APP_DATA_STORAGE_URL) ?
    getPublicUrl(path) : ["data", path.replace(/^\/+/g, "")].join("/");

export {
    getAssetDownloadUrl,
    getPublicUrl,
    useDetectScrollDirection,
    useIsOverflowDetected,
    usePrevious,
    useTimeout,
}